export type ProbeOutcome =
	| "ok"
	| "blocked"
	| "timeout"
	| "error"
	| "unavailable"

export interface ZeroStorageProbe {
	idb: ProbeOutcome
	idbMs: number | null
	idbError: string | null
	locks: ProbeOutcome
	heldLocks: number | null
	pendingLocks: number | null
	pendingLockNames: string[] | null
	usageBytes: number | null
	quotaBytes: number | null
}

export interface ZeroStorageProbeDeps {
	indexedDB?: IDBFactory | undefined
	locks?: LockManager | undefined
	storage?: Pick<StorageManager, "estimate"> | undefined
	timeoutMs?: number
	now?: () => number
}

export const ZERO_PROBE_TIMEOUT_MS = 3000
export const ZERO_PROBE_DB_NAME = "zero-storage-probe"
const PROBE_STORE = "probe"
const MAX_LOCK_NAMES = 10

const TIMEOUT = Symbol("timeout")

export async function resolveWithin<T>(
	promise: Promise<T>,
	timeoutMs: number,
): Promise<T | typeof TIMEOUT> {
	let timer: ReturnType<typeof setTimeout> | undefined
	const timeout = new Promise<typeof TIMEOUT>((resolve) => {
		timer = setTimeout(() => resolve(TIMEOUT), timeoutMs)
	})
	try {
		return await Promise.race([promise, timeout])
	} finally {
		if (timer) clearTimeout(timer)
	}
}

export function isProbeTimeout(value: unknown): boolean {
	return value === TIMEOUT
}

function errorText(error: unknown): string {
	if (error instanceof Error) return `${error.name}: ${error.message}`
	return String(error)
}

interface IdbResult {
	outcome: ProbeOutcome
	error: string | null
}

// A write, not just an open: a wedged sibling tab holding the version-change
// lock, and a profile with IDB switched off, both fail only once a transaction runs.
function probeIndexedDb(factory: IDBFactory): Promise<IdbResult> {
	return new Promise((resolve) => {
		let settled = false
		const settle = (outcome: ProbeOutcome, error: string | null) => {
			if (settled) return
			settled = true
			resolve({ outcome, error })
		}

		let request: IDBOpenDBRequest
		try {
			request = factory.open(ZERO_PROBE_DB_NAME, 1)
		} catch (error) {
			settle("error", errorText(error))
			return
		}

		request.onblocked = () => settle("blocked", "open blocked")
		request.onerror = () => settle("error", errorText(request.error))
		request.onupgradeneeded = () => {
			try {
				request.result.createObjectStore(PROBE_STORE)
			} catch (error) {
				settle("error", errorText(error))
			}
		}
		request.onsuccess = () => {
			const db = request.result
			try {
				const tx = db.transaction(PROBE_STORE, "readwrite")
				tx.objectStore(PROBE_STORE).put(Date.now(), "at")
				tx.oncomplete = () => {
					db.close()
					settle("ok", null)
				}
				tx.onabort = () => {
					db.close()
					settle("error", errorText(tx.error))
				}
				tx.onerror = () => {
					db.close()
					settle("error", errorText(tx.error))
				}
			} catch (error) {
				db.close()
				settle("error", errorText(error))
			}
		}
	})
}

export async function probeZeroStorage(
	deps: ZeroStorageProbeDeps = {},
): Promise<ZeroStorageProbe> {
	const timeoutMs = deps.timeoutMs ?? ZERO_PROBE_TIMEOUT_MS
	const now = deps.now ?? (() => Date.now())
	const factory =
		"indexedDB" in deps
			? deps.indexedDB
			: typeof indexedDB === "undefined"
				? undefined
				: indexedDB
	const locks =
		"locks" in deps
			? deps.locks
			: typeof navigator === "undefined"
				? undefined
				: navigator.locks
	const storage =
		"storage" in deps
			? deps.storage
			: typeof navigator === "undefined"
				? undefined
				: navigator.storage

	const probe: ZeroStorageProbe = {
		idb: "unavailable",
		idbMs: null,
		idbError: null,
		locks: "unavailable",
		heldLocks: null,
		pendingLocks: null,
		pendingLockNames: null,
		usageBytes: null,
		quotaBytes: null,
	}

	if (factory) {
		const startedAt = now()
		const result = await resolveWithin(probeIndexedDb(factory), timeoutMs)
		probe.idbMs = now() - startedAt
		if (isProbeTimeout(result)) {
			probe.idb = "timeout"
		} else {
			const { outcome, error } = result as IdbResult
			probe.idb = outcome
			probe.idbError = error
		}
	}

	if (locks) {
		try {
			const result = await resolveWithin(locks.query(), timeoutMs)
			if (isProbeTimeout(result)) {
				probe.locks = "timeout"
			} else {
				const snapshot = result as LockManagerSnapshot
				const held = snapshot.held ?? []
				const pending = snapshot.pending ?? []
				probe.locks = "ok"
				probe.heldLocks = held.length
				probe.pendingLocks = pending.length
				probe.pendingLockNames = pending
					.slice(0, MAX_LOCK_NAMES)
					.map((lock) => lock.name ?? "")
			}
		} catch (error) {
			probe.locks = "error"
			probe.pendingLockNames = [errorText(error)]
		}
	}

	if (storage) {
		try {
			const estimate = await resolveWithin(storage.estimate(), timeoutMs)
			if (!isProbeTimeout(estimate)) {
				const { usage, quota } = estimate as StorageEstimate
				probe.usageBytes = usage ?? null
				probe.quotaBytes = quota ?? null
			}
		} catch {
			// An estimate we cannot read tells us nothing; the IDB result already did.
		}
	}

	return probe
}
